import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTenantDrill,
  createTenantLessonPlan,
  createTenantStudentAssignment,
  createTenantStudentGoal,
  getTenantAthleteDevelopment,
  getTenantDevelopmentOverview,
  updateTenantDrill,
  updateTenantLessonPlan,
  updateTenantStudentAssignment,
  updateTenantStudentGoal,
} from '../lib/tenantDevelopment';
import type {
  TenantAthleteDevelopmentResponse,
  TenantDevelopmentOverviewResponse,
  TenantDrill,
  TenantDrillPayload,
  TenantLessonPlan,
  TenantLessonPlanPayload,
  TenantStudentAssignment,
  TenantStudentAssignmentPayload,
  TenantStudentAssignmentUpdatePayload,
  TenantStudentGoal,
  TenantStudentGoalPayload,
  TenantStudentGoalUpdatePayload,
} from '../types';
import { tenantDirectoryKeys } from './useTenantDirectory';

export const tenantDevelopmentKeys = {
  all: ['tenant-development'] as const,
  overview: () => [...tenantDevelopmentKeys.all, 'overview'] as const,
  athlete: (athleteId?: number | null) => [...tenantDevelopmentKeys.all, 'athlete', athleteId] as const,
};

export function useTenantDevelopmentOverview(enabled: boolean = true) {
  return useQuery<TenantDevelopmentOverviewResponse>({
    queryKey: tenantDevelopmentKeys.overview(),
    queryFn: getTenantDevelopmentOverview,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useTenantAthleteDevelopment(athleteId?: number | null, enabled: boolean = true) {
  return useQuery<TenantAthleteDevelopmentResponse>({
    queryKey: tenantDevelopmentKeys.athlete(athleteId),
    queryFn: () => getTenantAthleteDevelopment(athleteId as number),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && athleteId != null,
  });
}

export function useCreateTenantDrill() {
  const queryClient = useQueryClient();
  return useMutation<TenantDrill, Error, TenantDrillPayload>({
    mutationFn: createTenantDrill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.all });
    },
  });
}

export function useUpdateTenantDrill() {
  const queryClient = useQueryClient();
  return useMutation<TenantDrill, Error, { drillId: number; payload: TenantDrillPayload }>({
    mutationFn: ({ drillId, payload }) => updateTenantDrill(drillId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.all });
    },
  });
}

export function useCreateTenantLessonPlan() {
  const queryClient = useQueryClient();
  return useMutation<TenantLessonPlan, Error, TenantLessonPlanPayload>({
    mutationFn: createTenantLessonPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.all });
    },
  });
}

export function useUpdateTenantLessonPlan() {
  const queryClient = useQueryClient();
  return useMutation<TenantLessonPlan, Error, { lessonPlanId: number; payload: TenantLessonPlanPayload }>({
    mutationFn: ({ lessonPlanId, payload }) => updateTenantLessonPlan(lessonPlanId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.all });
    },
  });
}

export function useCreateTenantStudentAssignment() {
  const queryClient = useQueryClient();
  return useMutation<TenantStudentAssignment, Error, TenantStudentAssignmentPayload>({
    mutationFn: createTenantStudentAssignment,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.overview() });
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.athlete(variables.athleteId) });
      queryClient.invalidateQueries({ queryKey: tenantDirectoryKeys.athleteProgress(variables.athleteId) });
    },
  });
}

export function useUpdateTenantStudentAssignment() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantStudentAssignment,
    Error,
    { assignmentId: number; athleteId?: number | null; payload: TenantStudentAssignmentUpdatePayload }
  >({
    mutationFn: ({ assignmentId, payload }) => updateTenantStudentAssignment(assignmentId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.overview() });
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.athlete(variables.athleteId) });
      if (variables.athleteId != null) {
        queryClient.invalidateQueries({ queryKey: tenantDirectoryKeys.athleteProgress(variables.athleteId) });
      }
    },
  });
}

export function useCreateTenantStudentGoal() {
  const queryClient = useQueryClient();
  return useMutation<TenantStudentGoal, Error, TenantStudentGoalPayload>({
    mutationFn: createTenantStudentGoal,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.overview() });
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.athlete(variables.athleteId) });
      queryClient.invalidateQueries({ queryKey: tenantDirectoryKeys.athleteProgress(variables.athleteId) });
    },
  });
}

export function useUpdateTenantStudentGoal() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantStudentGoal,
    Error,
    { goalId: number; athleteId?: number | null; payload: TenantStudentGoalUpdatePayload }
  >({
    mutationFn: ({ goalId, payload }) => updateTenantStudentGoal(goalId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.overview() });
      queryClient.invalidateQueries({ queryKey: tenantDevelopmentKeys.athlete(variables.athleteId) });
      if (variables.athleteId != null) {
        queryClient.invalidateQueries({ queryKey: tenantDirectoryKeys.athleteProgress(variables.athleteId) });
      }
    },
  });
}
